import React, { useMemo, useRef, useState, useEffect, useLayoutEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowRight, FaBuilding, FaEye, FaFileAlt, FaMapMarkerAlt } from "react-icons/fa";
import { getClients } from "../../../../services/global_redux/globale_actions/Client.action";
import { getUnits } from "../../../../services/global_redux/globale_actions/Unit.action";
import {
  fetchGeneratedFormsList,
  Generated_forms_downloadDataInput,
  Generated_forms_downloadZip,
  Generated_forms_viewDataInput,
  Generated_forms_viewById,
} from "../../../../services/register_redux/register_actions/generateForm.action";
import GeneratedFormPdfPreview from "./GeneratedFormPdfPreview";
import { normalizeGeneratedKind } from "./GeneratedFormKindToggle";
import downloadIcon from "../../../../assets/register_assets/generatedForms/downloadIcon.png";
import bulkDownloadIcon from "../../../../assets/reUsableTemplates_assets/dowload_icon.png";
import "./Generated_Forms.css";

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const ALL_MONTHS = "all";

function clientDownloadErrorMessage(message, fallback = "Download failed") {
  const text = String(message || "").toLowerCase();
  if (text.includes("file not found") || text.includes("no file found")) {
    return "This document isn’t available to download right now.";
  }
  return (typeof message === "string" && message.trim()) ? message : fallback;
}

function toMonthFirstDay(value) {
  if (!value) return "";
  const s = String(value).trim();
  if (/^\d{4}-\d{2}$/.test(s)) {
    return `${s}-01`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return `${s.slice(0, 7)}-01`;
  }
  return s.slice(0, 10);
}

function toYearMonth(value) {
  if (!value) return "";
  const s = String(value).trim();
  const iso = s.match(/^(\d{4}-\d{2})/);
  if (iso) return iso[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthsFromGeneratedRows(rows) {
  return [...new Set(
    (Array.isArray(rows) ? rows : [])
      .map((row) => toYearMonth(row?.current_month))
      .filter(Boolean)
  )].sort();
}

function sameMonth(rowDate, filterDate) {
  if (!rowDate || !filterDate) return false;
  const a = String(rowDate).slice(0, 10);
  const b = String(filterDate).slice(0, 10);
  return a === b || a.slice(0, 7) === b.slice(0, 7);
}

function formatMonthLabel(isoDateStr) {
  if (!isoDateStr) return "";
  const s = String(isoDateStr).trim();
  const match = s.match(/^(\d{4})-(\d{2})/);
  if (match) {
    const monthName = MONTH_NAMES[parseInt(match[2], 10) - 1];
    return monthName ? `${monthName} ${match[1]}` : s;
  }
  return s;
}

function matchesInlinePeriod(rowDate, inlineList) {
  if (!inlineList) return false;
  if (inlineList.allMonths) {
    return String(toYearMonth(rowDate)).startsWith(`${inlineList.year}-`);
  }
  return sameMonth(rowDate, inlineList.date);
}

function periodHeading(inlineList) {
  if (!inlineList) return "";
  if (inlineList.allMonths) {
    return inlineList.year ? `All months ${inlineList.year}` : "All months";
  }
  return formatMonthLabel(inlineList.date);
}

function formatFormTitle(name) {
  return String(name || "Form")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function sanitizeFilePart(value) {
  return String(value || "")
    .replace(/[<>:"/\\|?*]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function clientFormDownloadName(formType, branchLabel, ext) {
  const form = sanitizeFilePart(formatFormTitle(formType));
  const branch = sanitizeFilePart(branchLabel || "branch");
  const stem = `${form}_${branch}`;
  if (!ext) return stem;
  return `${stem}.${String(ext).replace(/^\./, "")}`;
}

function unitLocation(unit) {
  return String(unit?.Location_City || unit?.location_city || "").trim();
}

function unitState(unit) {
  return String(unit?.state || unit?.state_name || "").trim();
}

function formatBranchPlace(unit) {
  const state = unitState(unit);
  const location = unitLocation(unit);
  if (state && location) return `${state}, ${location}`;
  return state || location || "";
}

function orgOptionLabel(client) {
  if (!client) return "";
  const id = String(client.client_id || "").trim();
  const name = String(client.client_name || "").trim();
  if (id && name) return `${id} - ${name}`;
  return id || name;
}

function branchOptionLabel(unit) {
  const name = String(unit?.unit_name || "").trim();
  const location = unitLocation(unit);
  const id = String(unit?.unit_id || "").trim();
  let place = "";
  if (name && location) place = `${name} (${location})`;
  else place = name || location || "";
  if (id && place) return `${id} - ${place}`;
  return id || place;
}

function formRowId(row) {
  return row?.id == null ? "" : String(row.id);
}

function getSessionUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || "{}") || {};
  } catch {
    return {};
  }
}

function getAssignedClientUnits() {
  try {
    const mapped = JSON.parse(getSessionUser().client_and_unit || "[]");
    return Array.isArray(mapped) ? mapped : [];
  } catch {
    return [];
  }
}

function getAssignedUnits(clientId) {
  const entry = getAssignedClientUnits().find(
    (item) => String(item.client_id) === String(clientId)
  );
  return (entry?.unit_id || []).map(String);
}

function getLockedClientId() {
  const stored = sessionStorage.getItem("selectedClient");
  if (stored && stored !== "null") return stored;
  return getAssignedClientUnits()[0]?.client_id || "";
}

function KindToggle({ value, onChange }) {
  return (
    <div className="generated-forms-kind-toggle" role="tablist" aria-label="Register forms or returns forms">
      <button
        type="button"
        role="tab"
        aria-selected={value === "register"}
        className={value === "register" ? "is-active" : ""}
        onClick={() => onChange("register")}
      >
        Register forms
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={value === "return"}
        className={value === "return" ? "is-active" : ""}
        onClick={() => onChange("return")}
      >
        Returns forms
      </button>
    </div>
  );
}

function GeneratedForm({ showSnackbar, setLoading = () => {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionUser = getSessionUser();
  const isClient = sessionUser?.base_role === "Client";
  const lockedClientId = isClient ? getLockedClientId() : "";
  const kind = isClient ? "register" : normalizeGeneratedKind(searchParams.get("kind"));
  const isReturn = kind === "return";

  const [clientOptions, setClientOptions] = useState([]);
  const [clientID, setClientID] = useState(isClient ? lockedClientId : "");
  const [unitOptions, setUnitOptions] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState("");
  const [allowedMonths, setAllowedMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [inlineList, setInlineList] = useState(null);
  const [clientForms, setClientForms] = useState([]);
  const [clientFormsLoading, setClientFormsLoading] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewFileName, setPreviewFileName] = useState("");
  const [previewUnavailable, setPreviewUnavailable] = useState(false);
  const [busyFormId, setBusyFormId] = useState(null);
  const [checkedItems, setCheckedItems] = useState({});
  const [zipBusy, setZipBusy] = useState(false);
  const monthLoadId = useRef(0);
  const kindReadyRef = useRef(false);
  const stickyRef = useRef(null);
  const deepLinkApplied = useRef(false);

  const yearOptions = useMemo(
    () => [...new Set(allowedMonths.map((ym) => ym.slice(0, 4)))].sort((a, b) => Number(b) - Number(a)),
    [allowedMonths]
  );

  const monthOptions = useMemo(
    () =>
      allowedMonths
        .filter((ym) => ym.startsWith(`${selectedYear}-`))
        .map((ym) => ym.slice(5, 7)),
    [allowedMonths, selectedYear]
  );

  const processDate =
    selectedYear && selectedMonth && selectedMonth !== ALL_MONTHS
      ? `${selectedYear}-${selectedMonth}`
      : "";
  const canViewPeriod = Boolean(selectedYear && selectedMonth);

  const lockedClient = clientOptions.find(
    (c) => String(c.client_id) === String(clientID || lockedClientId)
  );
  const orgName = lockedClient?.client_name || "Your organization";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await dispatch(getClients());
        if (cancelled) return;
        if (res.success && Array.isArray(res.data)) {
          setClientOptions(res.data);
        } else {
          setClientOptions([]);
          showSnackbar(res.message || "Could not load organizations.", "error");
          return;
        }

        if (isClient && lockedClientId) {
          setClientID(lockedClientId);
          sessionStorage.setItem("selectedClient", lockedClientId);
          const assigned = getAssignedUnits(lockedClientId);
          const response = await dispatch(getUnits(lockedClientId));
          if (cancelled) return;
          const rows = Array.isArray(response.data) ? response.data : [];
          setUnitOptions(rows.filter((u) => {
            if (String(u.client_id) !== String(lockedClientId)) return false;
            if (!assigned.length) return true;
            return assigned.includes(String(u.unit_id));
          }));
        }
      } catch {
        if (!cancelled) {
          setClientOptions([]);
          showSnackbar("Could not load organizations.", "error");
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isClient, lockedClientId]);

  useEffect(() => {
    if (!isClient || deepLinkApplied.current || !unitOptions.length) return;
    const unitId = searchParams.get("unit_id");
    const dateParam = searchParams.get("date");
    if (!unitId) return;
    const exists = unitOptions.some((u) => String(u.unit_id) === String(unitId));
    if (!exists) return;
    deepLinkApplied.current = true;
    const clientId = clientID || lockedClientId;
    setSelectedUnit(unitId);
    const ym = String(dateParam || "").slice(0, 7);
    if (/^\d{4}-\d{2}$/.test(ym)) {
      setSelectedYear(ym.slice(0, 4));
      setSelectedMonth(ym.slice(5, 7));
      setInlineList({
        client_id: clientId,
        unit_id: unitId,
        date: toMonthFirstDay(ym),
      });
    }
    if (clientId) loadAllowedMonths(clientId, unitId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClient, unitOptions, searchParams, clientID, lockedClientId]);

  const resetPeriod = () => {
    setSelectedYear("");
    setSelectedMonth("");
    setInlineList(null);
    setClientForms([]);
    setCheckedItems({});
  };

  const loadAllowedMonths = async (clientId, unitId) => {
    const req = ++monthLoadId.current;
    try {
      const response = await dispatch(
        fetchGeneratedFormsList({ client_id: clientId, unit_id: unitId, kind })
      );
      if (req !== monthLoadId.current) return;
      const months = monthsFromGeneratedRows(response?.data);
      setAllowedMonths(months);
      if (!months.length) {
        showSnackbar(
          isClient
            ? "No forms found for this branch."
            : isReturn
              ? "No generated returns found for this branch."
              : "No generated forms found for this branch.",
          "warning"
        );
      }
    } catch {
      if (req !== monthLoadId.current) return;
      setAllowedMonths([]);
      showSnackbar(
        isReturn
          ? "Could not load generated return months."
          : "Could not load generated form months.",
        "error"
      );
    }
  };

  const handleKindChange = (nextKind) => {
    const normalized = normalizeGeneratedKind(nextKind);
    if (normalized === kind) return;
    monthLoadId.current += 1;
    if (normalized === "return") setSearchParams({ kind: "return" });
    else setSearchParams({});
  };

  const handleClientChange = async (e) => {
    if (isClient) return;
    const id = e.target.value;
    setClientID(id);
    setSelectedUnit("");
    setAllowedMonths([]);
    resetPeriod();
    monthLoadId.current += 1;
    if (!id) {
      setUnitOptions([]);
      return;
    }
    sessionStorage.setItem("selectedClient", id);
    try {
      const response = await dispatch(getUnits(id));
      const rows = Array.isArray(response.data) ? response.data : [];
      setUnitOptions(rows.filter((u) => String(u.client_id) === String(id)));
    } catch {
      setUnitOptions([]);
      showSnackbar("Failed to load branches.", "error");
    }
  };

  const handleUnitChange = (e) => {
    const unitId = e.target.value;
    setSelectedUnit(unitId);
    setAllowedMonths([]);
    resetPeriod();
    if (!unitId) {
      monthLoadId.current += 1;
      return;
    }
    loadAllowedMonths(clientID, unitId);
  };

  useEffect(() => {
    if (isClient) return undefined;
    if (!kindReadyRef.current) {
      kindReadyRef.current = true;
      return undefined;
    }
    setAllowedMonths([]);
    resetPeriod();
    if (clientID && selectedUnit) {
      loadAllowedMonths(clientID, selectedUnit);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setSelectedMonth("");
    setInlineList(null);
    setClientForms([]);
    setCheckedItems({});
  };

  useEffect(() => {
    if (!isClient || !inlineList?.client_id || !inlineList?.unit_id) {
      return undefined;
    }
    if (!inlineList.allMonths && !inlineList.date) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setClientFormsLoading(true);
      try {
        const response = await dispatch(
          fetchGeneratedFormsList({
            client_id: inlineList.client_id,
            unit_id: inlineList.unit_id,
            kind: "register",
          })
        );
        if (cancelled) return;
        if (!response?.success) {
          showSnackbar(response?.message || "Failed to load forms.", "error");
          setClientForms([]);
          return;
        }
        const rows = Array.isArray(response.data) ? response.data : [];
        const filtered = rows
          .filter(
            (row) =>
              String(row.client_id) === String(inlineList.client_id) &&
              String(row.unit_id) === String(inlineList.unit_id) &&
              matchesInlinePeriod(row.current_month, inlineList)
          )
          .sort((a, b) =>
            String(toYearMonth(a.current_month)).localeCompare(String(toYearMonth(b.current_month)))
          );
        setClientForms(filtered);
        setCheckedItems({});
        if (!filtered.length) {
          showSnackbar("No forms for this period.", "warning");
        }
      } catch {
        if (!cancelled) {
          showSnackbar("Failed to load forms.", "error");
          setClientForms([]);
        }
      } finally {
        if (!cancelled) setClientFormsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClient, inlineList, dispatch]);

  const handleClientFormView = async (row) => {
    if (!row) return;
    const branch = unitOptions.find((unit) => String(unit.unit_id) === String(row.unit_id || selectedUnit));
    const fileName = clientFormDownloadName(
      row.form_type,
      branchOptionLabel(branch) || orgName,
      "pdf"
    );
    setBusyFormId(row.id || row.form_type);
    setLoading(true);
    setPreviewTitle(formatFormTitle(row.form_type));
    setPreviewFileName(fileName);
    setPreviewUnavailable(false);
    if (previewPdfUrl && String(previewPdfUrl).startsWith("blob:")) {
      URL.revokeObjectURL(previewPdfUrl);
    }
    setPreviewPdfUrl(null);
    try {
      const response = row.id
        ? await dispatch(Generated_forms_viewById(row.id))
        : await dispatch(
            Generated_forms_viewDataInput({
              client_id: row.client_id,
              unit_id: row.unit_id,
              date: toMonthFirstDay(row.current_month),
              form_type: row.form_type,
              format: "pdf",
            })
          );
      if (response.success && response.blob) {
        const named = new File([response.blob], fileName, { type: "application/pdf" });
        setPreviewPdfUrl(URL.createObjectURL(named));
      } else {
        setPreviewUnavailable(true);
      }
    } catch {
      setPreviewUnavailable(true);
    }
  };

  const handleClientFormDownload = async (row) => {
    if (!row?.id) return;
    const branch = unitOptions.find((unit) => String(unit.unit_id) === String(row.unit_id || selectedUnit));
    const branchLabel = branchOptionLabel(branch) || orgName;
    const downloadName = clientFormDownloadName(row.form_type, branchLabel);
    setBusyFormId(row.id);
    setLoading(true);
    try {
      const response = await dispatch(Generated_forms_downloadDataInput(row.id, downloadName));
      if (response.success) showSnackbar("Forms downloaded successfully", "success");
      else showSnackbar(clientDownloadErrorMessage(response.message), "warning");
    } catch {
      showSnackbar("An error occurred", "error");
    } finally {
      setBusyFormId(null);
      setLoading(false);
    }
  };

  const handleCheckAll = (e) => {
    const checked = e.target.checked;
    if (!checked) {
      setCheckedItems({});
      return;
    }
    const next = {};
    clientForms.forEach((row) => {
      const id = formRowId(row);
      if (id) next[id] = true;
    });
    setCheckedItems(next);
  };

  const handleCheckChange = (id) => {
    const key = String(id);
    setCheckedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleZipDownload = async () => {
    const selectedSet = new Set(
      Object.entries(checkedItems)
        .filter(([, on]) => on)
        .map(([id]) => String(id))
    );
    const rows = clientForms.filter((row) => selectedSet.has(formRowId(row)));
    if (!rows.length) {
      showSnackbar("select the row", "warning");
      return;
    }
    if (rows.length === 1) {
      await handleClientFormDownload(rows[0]);
      return;
    }

    const ids = rows.map((row) => row.id).filter((id) => id != null);
    if (!ids.length) {
      showSnackbar("select the row", "warning");
      return;
    }

    const branch = unitOptions.find((unit) => String(unit.unit_id) === String(selectedUnit));
    const branchLabel = sanitizeFilePart(branchOptionLabel(branch) || orgName || "forms");
    const period = (
      inlineList?.allMonths
        ? periodHeading(inlineList)
        : formatMonthLabel(inlineList?.date)
    ).replace(/\s+/g, " ");
    const zipName = `${branchLabel}_${period || "forms"}_${ids.length}_forms.zip`;

    setZipBusy(true);
    setLoading(true);
    try {
      const response = await dispatch(Generated_forms_downloadZip(ids, zipName));
      if (response.success) {
        showSnackbar("Forms downloaded successfully", "success");
      } else {
        showSnackbar(clientDownloadErrorMessage(response.message), "warning");
      }
    } catch {
      showSnackbar("An error occurred", "error");
    } finally {
      setZipBusy(false);
      setLoading(false);
    }
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    if (isClient) {
      if (!clientID || !selectedUnit || !selectedYear || !selectedMonth) {
        showSnackbar("Please select branch, year, and month.", "error");
        return;
      }
      if (selectedMonth === ALL_MONTHS) {
        if (!allowedMonths.some((ym) => ym.startsWith(`${selectedYear}-`))) {
          showSnackbar("No released forms for this year.", "warning");
          return;
        }
        setInlineList({
          client_id: clientID,
          unit_id: selectedUnit,
          date: selectedYear,
          year: selectedYear,
          allMonths: true,
        });
        return;
      }
      if (!allowedMonths.includes(processDate)) {
        showSnackbar("Select a month that already has released forms.", "error");
        return;
      }
      setInlineList({
        client_id: clientID,
        unit_id: selectedUnit,
        date: toMonthFirstDay(processDate),
        year: selectedYear,
        allMonths: false,
      });
      return;
    }
    if (!clientID || !selectedUnit || !processDate) {
      showSnackbar("Please select organization, branch, year, and month.", "error");
      return;
    }
    if (!allowedMonths.includes(processDate)) {
      showSnackbar(
        isReturn
          ? "Select a month that already has generated returns."
          : "Select a month that already has generated forms.",
        "error"
      );
      return;
    }
    const date = toMonthFirstDay(processDate);
    const kindQuery = isReturn ? "&kind=return" : "";
    navigate(
      `/generated-form/list?client_id=${encodeURIComponent(clientID)}&unit_id=${encodeURIComponent(selectedUnit)}&date=${encodeURIComponent(date)}${kindQuery}`
    );
  };

  const periodDisabled = !selectedUnit || !allowedMonths.length;
  const selectedBranch = unitOptions.find(
    (unit) => String(unit.unit_id) === String(selectedUnit)
  );
  const selectedBranchLabel = (() => {
    const name = String(selectedBranch?.unit_name || "").trim();
    const location = unitLocation(selectedBranch);
    if (name && location) return `${name} (${location})`;
    return name || location || "";
  })();
  const branchPlace = formatBranchPlace(selectedBranch);
  const orgState = lockedClient?.state_name || unitState(selectedBranch) || "";
  const selectedIds = Object.keys(checkedItems).filter((id) => checkedItems[id]);
  const allChecked =
    clientForms.length > 0 &&
    clientForms.every((row) => {
      const id = formRowId(row);
      return id && checkedItems[id];
    });
  const actionsLocked = !!busyFormId || zipBusy;

  useLayoutEffect(() => {
    if (!isClient) return undefined;
    const el = stickyRef.current;
    const shell = el?.parentElement;
    if (!el || !shell) return undefined;
    const apply = () => {
      shell.style.setProperty("--client-sticky-h", `${el.offsetHeight}px`);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    window.addEventListener("resize", apply);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, [isClient, inlineList, clientFormsLoading, clientForms.length]);

  const yearMonthFields = (
    <div className="generated-forms-year-month-row">
      <div className="generated-forms-field">
        <label className="form-label">Year</label>
        <select
          className="form-select"
          value={selectedYear}
          onChange={(ev) => handleYearChange(ev.target.value)}
          disabled={periodDisabled}
        >
          <option value="">Select year</option>
          {yearOptions.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>
      <div className="generated-forms-field">
        <label className="form-label">Month</label>
        <select
          className="form-select"
          value={selectedMonth}
          onChange={(ev) => setSelectedMonth(ev.target.value)}
          disabled={periodDisabled || !selectedYear}
        >
          <option value="">Select month</option>
          {monthOptions.map((mm) => (
            <option key={mm} value={mm}>
              {MONTH_NAMES[parseInt(mm, 10) - 1]}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  if (isClient) {
    return (
      <div className="container-fluid generated-forms-page generated-forms-page-client">
        <div className="generated-forms-client-shell">
          <div className="generated-forms-client-title">
            <h1>Select your branch, year, and month to open the related forms.</h1>
          </div>

          <div className="generated-forms-client-card">
            <div className="generated-forms-client-org" aria-label="Organization">
              <div className="generated-forms-client-org-icon">
                <FaBuilding />
              </div>
              <div className="generated-forms-client-org-copy">
                <span>Organization</span>
                <strong>{orgName}</strong>
                {orgState ? <em>{orgState}</em> : null}
              </div>
            </div>
          </div>

          <div className="generated-forms-client-sticky-block" ref={stickyRef}>
            <form className="generated-forms-client-form" onSubmit={handleFilterSubmit} data-tour="forms-filters">
              <div className="generated-forms-client-field">
                <label className="form-label" htmlFor="client-branch-select">
                  <FaMapMarkerAlt /> Branch
                </label>
                <select
                  id="client-branch-select"
                  className="form-select"
                  value={selectedUnit}
                  onChange={handleUnitChange}
                  disabled={!clientID}
                >
                  <option value="">Select branch</option>
                  {unitOptions.map((unit) => (
                    <option key={unit.unit_id} value={unit.unit_id}>
                      {branchOptionLabel(unit)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="generated-forms-client-field">
                <label className="form-label" htmlFor="client-year-select">Year</label>
                <select
                  id="client-year-select"
                  className="form-select"
                  value={selectedYear}
                  onChange={(ev) => handleYearChange(ev.target.value)}
                  disabled={periodDisabled}
                >
                  <option value="">Select year</option>
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div className="generated-forms-client-field">
                <label className="form-label" htmlFor="client-month-select">Month</label>
                <select
                  id="client-month-select"
                  className="form-select"
                  value={selectedMonth}
                  onChange={(ev) => {
                    setSelectedMonth(ev.target.value);
                    setInlineList(null);
                    setClientForms([]);
                    setCheckedItems({});
                  }}
                  disabled={periodDisabled || !selectedYear}
                >
                  <option value="">Select month</option>
                  <option value={ALL_MONTHS}>All months</option>
                  {monthOptions.map((mm) => (
                    <option key={mm} value={mm}>
                      {MONTH_NAMES[parseInt(mm, 10) - 1]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="generated-forms-client-actions">
                <button
                  type="submit"
                  className="btn generated-forms-client-submit"
                  disabled={!clientID || !selectedUnit || !canViewPeriod}
                >
                  View forms <FaArrowRight />
                </button>
              </div>
            </form>

            {inlineList && !clientFormsLoading && clientForms.length > 0 ? (
              <div className="generated-forms-client-list-head">
                <strong>
                  {selectedBranchLabel}
                  {periodHeading(inlineList) ? ` · ${periodHeading(inlineList)}` : ""}
                </strong>
                <div className="generated-forms-client-list-tools">
                  <button
                    type="button"
                    className="generated-forms-client-bulk-download"
                    title={selectedIds.length ? `Download ${selectedIds.length} selected` : "select the row"}
                    onClick={handleZipDownload}
                    disabled={zipBusy}
                  >
                    <img src={bulkDownloadIcon} alt="Download" />
                  </button>
                  <span>{clientForms.length} {clientForms.length === 1 ? "form" : "forms"}</span>
                </div>
              </div>
            ) : null}
          </div>

          {inlineList ? (
            <div className={`generated-forms-client-workspace${clientFormsLoading || clientForms.length === 0 ? "" : " is-filled"}`}>
              {clientFormsLoading ? (
                <div className="generated-forms-client-list-empty">
                  <p>Loading forms…</p>
                </div>
              ) : clientForms.length === 0 ? (
                <div className="generated-forms-client-list-empty">
                  <div className="generated-forms-client-workspace-icon">
                    <FaFileAlt />
                  </div>
                  <p>{inlineList.allMonths ? "No forms for this branch and year." : "No forms for this branch and month."}</p>
                </div>
              ) : (
                <div className="generated-forms-client-table-scroll" data-tour="forms-list">
                  <table className="generated-forms-client-list">
                    <colgroup>
                      <col className="generated-forms-client-col-check" />
                      <col className="generated-forms-client-col-form" />
                      <col className="generated-forms-client-col-location" />
                      <col className="generated-forms-client-col-month" />
                      <col className="generated-forms-client-col-action" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="generated-forms-client-check">
                          <input
                            type="checkbox"
                            checked={allChecked}
                            onChange={handleCheckAll}
                            disabled={actionsLocked}
                            aria-label="Select all forms"
                          />
                        </th>
                        <th>Form</th>
                        <th>Location</th>
                        <th>Month</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientForms.map((row) => {
                        const rowId = formRowId(row);
                        const busy = busyFormId === row.id || busyFormId === row.form_type;
                        return (
                          <tr key={row.id || row.form_type}>
                            <td className="generated-forms-client-check">
                              <input
                                type="checkbox"
                                checked={!!checkedItems[rowId]}
                                onChange={() => handleCheckChange(rowId)}
                                disabled={actionsLocked || !rowId}
                                aria-label={`Select ${formatFormTitle(row.form_type)}`}
                              />
                            </td>
                            <td>{formatFormTitle(row.form_type)}</td>
                            <td>{branchPlace || "—"}</td>
                            <td>{formatMonthLabel(row.current_month)}</td>
                            <td className="generated-forms-client-actions-cell">
                              <div className="generated-forms-client-row-actions">
                                <button
                                  type="button"
                                  title="View PDF"
                                  disabled={actionsLocked}
                                  onClick={() => handleClientFormView(row)}
                                >
                                  <FaEye />
                                </button>
                                <button
                                  type="button"
                                  title="Download"
                                  disabled={actionsLocked}
                                  onClick={() => handleClientFormDownload(row)}
                                >
                                  <img src={downloadIcon} alt="Download" />
                                </button>
                              </div>
                              {busy || zipBusy ? <span className="visually-hidden">Working</span> : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
          <div className="generated-forms-client-workspace">
            <div className="generated-forms-client-docs" aria-hidden="true">
              <div className="generated-forms-client-doc">
                <span />
                <span />
                <span />
                <span />
              </div>
              <div className="generated-forms-client-doc is-active">
                <span />
                <span />
                <span />
                <span />
              </div>
              <div className="generated-forms-client-doc">
                <span />
                <span />
                <span />
                <span />
              </div>
            </div>
            <div className="generated-forms-client-workspace-copy">
              <div className="generated-forms-client-workspace-icon">
                <FaFileAlt />
              </div>
              <p>Forms will appear here after you click View forms.</p>
            </div>
          </div>
          )}
        </div>
        <GeneratedFormPdfPreview
          pdfUrl={previewPdfUrl}
          title={previewTitle}
          fileName={previewFileName}
          unavailable={previewUnavailable}
          onReady={() => {
            setBusyFormId(null);
            setLoading(false);
          }}
          onClose={() => {
            if (previewPdfUrl && String(previewPdfUrl).startsWith("blob:")) {
              URL.revokeObjectURL(previewPdfUrl);
            }
            setPreviewPdfUrl(null);
            setPreviewUnavailable(false);
            setPreviewTitle("");
            setPreviewFileName("");
            setBusyFormId(null);
            setLoading(false);
          }}
        />
      </div>
    );
  }

  return (
    <div className="container-fluid generated-forms-page">
      <div className="card generated-forms-card">
        <div className="card-body">
          <div className="generated-forms-header">
            <h1>{isReturn ? "Generated Returns" : "Generated Form"}</h1>
            <KindToggle value={kind} onChange={handleKindChange} />
            <p>
              {isReturn
                ? "Choose organization, branch, and a month that already has generated returns."
                : "Choose organization, branch, and a month that already has generated forms."}
            </p>
          </div>
          <form onSubmit={handleFilterSubmit}>
            <div className="generated-forms-filter-row">
              <div className="generated-forms-org-branch-row">
                <div className="generated-forms-field">
                  <label className="form-label">Organization</label>
                  <select className="form-select" value={clientID} onChange={handleClientChange}>
                    <option value="">Select organization</option>
                    {clientOptions.map((c) => (
                      <option key={c.client_id} value={c.client_id}>
                        {orgOptionLabel(c)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="generated-forms-field">
                  <label className="form-label">Branch</label>
                  <select
                    className="form-select"
                    value={selectedUnit}
                    onChange={handleUnitChange}
                    disabled={!clientID}
                  >
                    <option value="">Select branch</option>
                    {unitOptions.map((unit) => (
                      <option key={unit.unit_id} value={unit.unit_id}>
                        {branchOptionLabel(unit)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {yearMonthFields}
            </div>
            <button
              type="submit"
              className="btn btn-primary generated-forms-submit"
              disabled={!clientID || !selectedUnit || !processDate}
            >
              {isReturn ? "View generated returns" : "View generated forms"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default GeneratedForm;
